import { useAuth } from '@Auth/useAuth';
import { Icon } from '@assets/Icon';
import { CenteredPage } from '@common/CenteredPage';
import { InputField } from '@common/InputField';
import { InstallPWAButton } from '@common/InstallButton/InstallButton';
import {
  Box,
  Button,
  Divider,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { onErrorShow } from '@utils/onErrorShow';
import { onInfoShow } from '@utils/onInfoShow';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { AuthClient } from '@Clients/AuthClient';

export function Login() {
  const { signIn } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {}
  );

  const validateInput = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setLoading(true);
    if (!validateInput()) {
      onErrorShow('Please fix the errors before proceeding.');
      setLoading(false);
      return;
    }

    try {
      await AuthClient.loginWithPassword(email, password);
      onInfoShow('Login successful!');
      navigate('/products/all-products'); // Redirect to products page
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        onErrorShow(error.message);
      } else {
        onErrorShow('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <CenteredPage>
      <Typography
        variant="h1"
        sx={{
          fontWeight: 700,
        }}
      >
        Sku<span style={{ color: theme.palette.primary.main }}>ey</span>
      </Typography>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h1">Welcome back!</Typography>
        <InstallPWAButton />
      </Box>
      <Typography
        variant="body2"
        color={theme.palette.text.secondary}
        sx={{
          marginBottom: 2,
        }}
      >
        Please login to continue
      </Typography>
      <Button
        size="medium"
        variant="outlined"
        type="button"
        fullWidth
        startIcon={<Icon name="google" />}
        onClick={async () => await signIn()}
      >
        Continue with Google
      </Button>{' '}
      <Divider> or</Divider>
      <Typography variant="body2" color={theme.palette.text.secondary}>
        Email
      </Typography>
      <InputField
        type="email"
        autoComplete="off"
        placeholder="you@example.com"
        autoCapitalize="off"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        errorMessage={errors.email}
      />
      <Typography variant="body2" color={theme.palette.text.secondary}>
        Password
      </Typography>
      <InputField
        type="password"
        autoComplete="off"
        placeholder="••••••••"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        errorMessage={errors.password}
      />
      <Button
        disabled={loading}
        startIcon={
          loading ? (
            <ClipLoader color={theme.palette.primary.dark} size={20} />
          ) : null
        }
        type="submit"
        size="medium"
        color="primary"
        variant="contained"
        fullWidth
        sx={{
          marginTop: 2.5,
        }}
        onClick={handleLogin}
      >
        Login
      </Button>
      <Typography variant="body2">
        Don't have an account? <Link href="/sign-up">Sign up</Link>
      </Typography>
    </CenteredPage>
  );
}
